import { useCallback, useContext, useEffect, useState } from 'react'
import PrimaryButton from '../../../global/components/buttons/primary-button/PrimaryButton'
import { ToastNotificationContext } from '../../../global/context/toast-context/ToastNotificationContext'
import { fetchData } from '../../../global/utils/fetch'
import {
  getDocuments,
  deleteDocument,
  getCategories,
  deleteCategoryApi,
} from '../../api'
import DocumentTable, { DocumentListItem } from '../document-table/DocumentTable'
import UploadModal from '../upload-modal/UploadModal'
import CategoryTable, { CategoryListItem } from '../category-table/CategoryTable'
import CategoryModal from '../category-modal/CategoryModal'
import PromptEditor from '../prompt-editor/PromptEditor'
import ChatPanel from '../chat-panel/ChatPanel'
import styles from './AIAssistantContent.module.css'

type LeftTab = 'prompts' | 'categories' | 'documents'

function AIAssistantContent() {
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)
  const [leftTab, setLeftTab] = useState<LeftTab>('prompts')

  // Categories state
  const [categories, setCategories] = useState<CategoryListItem[]>([])
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(true)
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<CategoryListItem | null>(null)

  // Documents state
  const [documents, setDocuments] = useState<DocumentListItem[]>([])
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(true)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)

  const loadCategories = useCallback(async () => {
    setIsCategoriesLoading(true)
    try {
      const data = await fetchData<CategoryListItem[]>(getCategories())
      setCategories(data)
    } catch (error: any) {
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: error.message || 'Failed to load categories.',
        severity: 'error',
      })
    } finally {
      setIsCategoriesLoading(false)
    }
  }, [])

  const loadDocuments = useCallback(async () => {
    setIsDocumentsLoading(true)
    try {
      const data = await fetchData<DocumentListItem[]>(getDocuments())
      setDocuments(data)
    } catch (error: any) {
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: error.message || 'Failed to load documents.',
        severity: 'error',
      })
    } finally {
      setIsDocumentsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadCategories()
    loadDocuments()
  }, [loadCategories, loadDocuments])

  function handleEditCategory(category: CategoryListItem) {
    setEditingCategory(category)
    setIsCategoryModalOpen(true)
  }

  async function handleDeleteCategory(objectId: string) {
    if (!window.confirm('Are you sure you want to delete this category?')) return
    try {
      await fetchData(deleteCategoryApi(objectId))
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Category deleted.',
        severity: 'success',
      })
      loadCategories()
    } catch (error: any) {
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: error.message || 'Failed to delete category.',
        severity: 'error',
      })
    }
  }

  async function handleDeleteDocument(documentObjectId: string) {
    if (!window.confirm('Are you sure you want to delete this document?')) return
    try {
      await fetchData(deleteDocument(documentObjectId))
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Document deleted.',
        severity: 'success',
      })
      loadDocuments()
    } catch (error: any) {
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: error.message || 'Failed to delete document.',
        severity: 'error',
      })
    }
  }

  function renderLeftContent() {
    switch (leftTab) {
      case 'prompts':
        return <PromptEditor />
      case 'categories':
        return (
          <div style={{ padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
              <PrimaryButton
                buttonName='Add Category'
                onClick={() => {
                  setEditingCategory(null)
                  setIsCategoryModalOpen(true)
                }}
              />
            </div>
            <CategoryTable
              categories={categories}
              isLoading={isCategoriesLoading}
              onEdit={handleEditCategory}
              onDelete={handleDeleteCategory}
            />
            <CategoryModal
              isOpen={isCategoryModalOpen}
              onClose={() => setIsCategoryModalOpen(false)}
              onSaveComplete={loadCategories}
              editingCategory={editingCategory}
            />
          </div>
        )
      case 'documents':
        return (
          <div style={{ padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
              <PrimaryButton
                buttonName='Upload Document'
                onClick={() => setIsUploadModalOpen(true)}
              />
            </div>
            <DocumentTable
              documents={documents}
              isLoading={isDocumentsLoading}
              onDelete={handleDeleteDocument}
            />
            <UploadModal
              isOpen={isUploadModalOpen}
              onClose={() => setIsUploadModalOpen(false)}
              onUploadComplete={loadDocuments}
            />
          </div>
        )
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.leftPanel}>
        <div className={styles.tabBar}>
          <button
            className={leftTab === 'prompts' ? styles.tabActive : styles.tab}
            onClick={() => setLeftTab('prompts')}
          >
            Prompts
          </button>
          <button
            className={leftTab === 'categories' ? styles.tabActive : styles.tab}
            onClick={() => setLeftTab('categories')}
          >
            Categories
          </button>
          <button
            className={leftTab === 'documents' ? styles.tabActive : styles.tab}
            onClick={() => setLeftTab('documents')}
          >
            Documents
          </button>
        </div>
        <div className={styles.tabContent}>{renderLeftContent()}</div>
      </div>

      <div className={styles.rightPanel}>
        <ChatPanel />
      </div>
    </div>
  )
}

export default AIAssistantContent
